import { ResultPagDTO } from '@/interfaces/base-dto';
import { IBusinessUnit } from '@/interfaces/business-units';
import IClient from '@/interfaces/client';
import { EfetivacaoAnaliseDocsParm, ICurrentUser, IUser, LoginAtivacaoParmDTO, LoginPreAtivacaoDadosDTO, LoginUnidadesParmDTO, LoginUsuarioParmDTO, UsuarioAnaliseEfetivacaoResult, UsuarioFiltroParmDTO } from '@/interfaces/user';
import { ENDPOINTS } from '@/settings/api-settings';
import { AxiosResponse } from 'axios';
import { cloneDeep } from 'lodash';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { apiClient } from './api';
import { setToken } from './session-data';


export function listUserStatusActivationOptions(): Observable<any> {
  return from(apiClient.get(ENDPOINTS.USER_STATUS_ACTIVATION_OPTIONS)).pipe(
    map((r: any) => {
      return !!r && r.data ? r.data : [];
    }),
  );
}

function getUsers(filters: UsuarioFiltroParmDTO): Observable<ResultPagDTO<any>> {
  return from(apiClient.get(ENDPOINTS.USERS, filters)).pipe(
    map((r: any) => {
      return !!r && r.data ? r.data : [];
    }),
  );
}

function getUser(id: string): Observable<IUser> {
  return from(apiClient.get(`${ENDPOINTS.USERS}/${id}`)).pipe(
    map((r: any) => r.data),
  );
}

export function getUserActivationHist(id: string): Observable<any[]> {
  return from(apiClient.get(`${ENDPOINTS.USERS}/${id}/historico/ativacao`)).pipe(
    map((r: any) => r.data),
  );
}

function saveUser(payload: LoginUsuarioParmDTO): Observable<any> {
  return from(apiClient.post(ENDPOINTS.USERS, payload)).pipe(
    map((r: any) => r.data),
  );
}

function updateUser(payload: LoginUsuarioParmDTO): Observable<any> {
  return from(apiClient.put(ENDPOINTS.USERS, payload)).pipe(
    map((r: any) => r.data),
  );
}

export function getPreActivateUserDatas(id: string): Observable<LoginPreAtivacaoDadosDTO> {
  return from(apiClient.get(`${ENDPOINTS.USERS}/${id}/ativacao`)).pipe(
    map((r: any) => r.data),
  );
}

export function activateUser(payload: LoginAtivacaoParmDTO): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.USERS}/ativacao`, payload),
  ).pipe(map((r: any) => r.data));
}

export function alterUnitUser(payload: LoginUnidadesParmDTO): Observable<any> {
  return from(
    apiClient.put(`${ENDPOINTS.USERS}/unidades-cliente`, payload),
  ).pipe(map((r: any) => r.data));
}

export function sendAccessEmail(id: number): Observable<string> {
  return from(
    apiClient.post(`${ENDPOINTS.USERS}/${id}/acesso/email`),
  ).pipe(map((r: any) => r.data));
}

function updateCurrentUser(payload: ICurrentUser): Observable<any> {
  return from(apiClient.put(ENDPOINTS.USER_LOGGED, payload)).pipe(
    map((r: any) => r.data),
  );
}

function getBusinessUnitsUser(): Observable<IBusinessUnit[]> {
  return from(apiClient.get(ENDPOINTS.BUSINESS_UNITS_USER)).pipe(
    map((r: any) => r.data),
  );
}

export function listLeaders(idLogin: number, idsBusinessUnits: number[]): Observable<any[]> {
  return from(apiClient.get(ENDPOINTS.USERS_LEADERS, {idLoginAlteracao: idLogin, idsEmpresas: idsBusinessUnits})).pipe(
    map((r: any) => r.data),
  );
}

export function listSubordinates(multiLevel: boolean, meIncluded: boolean): Observable<any[]> {
  return from(apiClient.get(ENDPOINTS.USERS_SUBORDINATES, {multiNivel: multiLevel, logadoIncluso: meIncluded})).pipe(
    map((r: any) => r.data),
  );
}

export function listGeoAreas(idsBusinessUnits: number[]): Observable<any[]> {
  return from(apiClient.get(ENDPOINTS.USERS_GEO_AREAS, { idsEmpresas: idsBusinessUnits})).pipe(
    map((r: any) => r.data),
  );
}

export function listUserProfiles(type=null): Observable<any[]> {
  return from(apiClient.get(ENDPOINTS.USERS_PROFILES, {tipoAcesso: type})).pipe(
    map((r: any) => r.data),
  );
}

export function listUserLoginTypes(): Observable<any[]> {
  return from(apiClient.get(ENDPOINTS.USERS_LOGIN_TYPES)).pipe(
    map((r: any) => r.data),
  );
}

function getUserClients(): Observable<IClient[]> {
  return from(apiClient.get(ENDPOINTS.USER_SESSION_DATA)).pipe(
    map((r: any) => {
      if (!r.data || !r.data.unidadesCliente) {
        return [];
      }

      return r.data.unidadesCliente.reduce((clients, unit) => {
        const clientIndex = clients.findIndex(
          (item) => item.id === unit.cliente.id,
        );

        const newUnit = cloneDeep(unit);
        delete newUnit.cliente.unidades;

        if (clientIndex === -1) {
          unit.cliente.unidades = [newUnit];
          clients.push(unit.cliente);
        } else {
          clients[clientIndex].unidades.push(newUnit);
        }

        return clients;
      }, []);
    }),
  );
}

function setSelectedClient(payload): Observable<any> {
  return updateSessionData(null, payload.idUnidadeCliente);
}

function setSelectedBusinessUnit(idEmpresa: number): Observable<any> {
  return updateSessionData(idEmpresa, null);
}

function updateSessionData(idEmpresa: number, idUnidadeCliente: number): Observable<any> {  
  return from(apiClient.put(ENDPOINTS.USER_SESSION_DATA, { idEmpresa, idUnidadeCliente })).pipe(
    map((response: any) => {
      if (!response) {
        return {};
      }
      const token = !!response.headers ? response.headers.authorization : null;
      setToken(token);

      return !!response.data ? response.data : {};
    }),
  );
}

export function findUserDocsAnalysis(token: string): Observable<UsuarioAnaliseEfetivacaoResult> {
  return from(apiClient.get(`${ENDPOINTS.USER_DOCS_ANALYSIS}/${token}`)).pipe(
    map((r: any) => r.data),
  );
}

export function downloadUserDocAnalysis(token: string, idDoc: number): Observable<AxiosResponse> {
  return from(apiClient.download(`${ENDPOINTS.USER_DOCS_ANALYSIS}/${token}/${idDoc}`));
}

export function saveUserDocAnalysis(parm: EfetivacaoAnaliseDocsParm): Observable<string> {
  return from(apiClient.post(`${ENDPOINTS.USER_DOCS_ANALYSIS}`, parm)).pipe(
    map((r: any) => r.data),
  );
}

export function resendPreActivationUserEmail(token: string): Observable<string> {
  return from(apiClient.post(`${ENDPOINTS.USER_PRE_ACTIVATION_EMAIL}/${token}`)).pipe(
    map((r: any) => r.data),
  );
}



export {
  getUsers,
  getUser,
  saveUser,
  updateUser,
  updateCurrentUser,
  getUserClients,
  setSelectedClient,
  setSelectedBusinessUnit,
  getBusinessUnitsUser,
};

